import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "button", "status", "waveform"]
  static values = {
    recording: Boolean,
    hasPermission: Boolean
  }

  connect() {
    this.mediaRecorder = null
    this.audioChunks = []
    this.isRecording = false
    this.checkMicrophonePermission()
  }

  async checkMicrophonePermission() {
    try {
      const result = await navigator.permissions.query({ name: 'microphone' })
      this.hasPermissionValue = result.state === 'granted'
      this.updateButtonState()
      result.addEventListener('change', () => {
        this.hasPermissionValue = result.state === 'granted'
        this.updateButtonState()
      })
    } catch (error) {
      console.error('Error checking microphone permission:', error)
      this.hasPermissionValue = false
      this.updateButtonState()
    }
  }

  updateButtonState() {
    if (!this.hasPermissionValue) {
      this.buttonTarget.classList.add('opacity-50', 'cursor-not-allowed')
      this.buttonTarget.classList.remove('hover:bg-gray-200')
      this.statusTarget.textContent = 'Microphone access required'
    } else {
      this.buttonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      this.buttonTarget.classList.add('hover:bg-gray-200')
      this.statusTarget.textContent = ''
    }
  }

  toggleRecording() {
    if (this.isRecording) {
      this.stopRecording()
    } else {
      this.startRecording()
    }
  }

  async startRecording() {
    if (!this.hasPermissionValue) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.hasPermissionValue = true
        this.updateButtonState()
        stream.getTracks().forEach(track => track.stop())
      } catch (error) {
        console.error('Error accessing microphone:', error)
        alert('Please allow microphone access in your browser settings to use voice input.')
        return
      }
    }

    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        this.mediaRecorder = new MediaRecorder(stream)
        this.audioChunks = []

        this.mediaRecorder.ondataavailable = (event) => {
          this.audioChunks.push(event.data)
        }

        this.mediaRecorder.onstop = () => {
          const audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' })
          this.transcribeAudio(audioBlob)
        }

        this.mediaRecorder.start()
        this.isRecording = true
        this.buttonTarget.classList.add('bg-red-100', 'hover:bg-red-200')
        this.statusTarget.textContent = "Recording..."
        this.inputTarget.classList.add('hidden')
        this.waveformTarget.classList.remove('hidden')
      })
      .catch(error => {
        console.error('Error accessing microphone:', error)
        this.statusTarget.textContent = "Error accessing microphone"
      })
  }

  stopRecording() {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop()
      this.isRecording = false
      this.buttonTarget.classList.remove('bg-red-100', 'hover:bg-red-200')
      this.statusTarget.textContent = ""
      this.inputTarget.classList.remove('hidden')
      this.waveformTarget.classList.add('hidden')
    }
  }

  async transcribeAudio(audioBlob) {
    const formData = new FormData()
    formData.append('audio', audioBlob)

    try {
      this.statusTarget.textContent = "Transcribing..."
      const response = await fetch('/api/v1/transcribe', {
        method: 'POST',
        body: formData
      })

      if (!response.ok) {
        throw new Error('Transcription failed')
      }

      const data = await response.json()
      this.inputTarget.value = data.text
      this.statusTarget.textContent = "Transcription complete"
    } catch (error) {
      console.error('Error transcribing audio:', error)
      this.statusTarget.textContent = "Error transcribing audio"
    }
  }
}
